import type { ComsStore, ComsStorePersist } from '@mfe-4cs/state/ComsStore';
import SessionStorage from '@mfe-4cs/util/SessionStorage';

const STORAGE_NAME = 'tef-coms4cs-state';

/**
 * Manages the state persistence and retrieval for ComsStore
 */
export class Coms4csState {
  /**
   * The key used to store and retrieve state from SessionStorage
   */
  static readonly STORAGE_NAME = STORAGE_NAME;

  /**
   * Reference to the ComsStore instance that this state manager is associated with
   */
  private comsStore: ComsStore;

  /**
   * Creates a new instance of Coms4csState
   *
   * @param comsStore - The ComsStore instance to manage state for
   */
  constructor(comsStore: ComsStore) {
    this.comsStore = comsStore;
  }

  /**
   * Saves the state to SessionStorage or clears it if no state is provided
   *
   * @param state - The state to save, or undefined to clear the state
   */
  private static setState(state?: ComsStorePersist) {
    SessionStorage.setItem(STORAGE_NAME, state);
  }

  /**
   * Retrieves the current state from SessionStorage
   *
   * If no state exists, creates a default state with empty page array and current identity and config
   * If the page property is not an array, converts it to an array with the page as its only element
   *
   * @return {ComsStorePersist} The current state
   */
  getState() {
    const state = SessionStorage.getItem<ComsStorePersist>(STORAGE_NAME);

    if (!state) {
      return {
        page: [],
        identity: this.comsStore.identity!,
        config: this.comsStore.config!,
      };
    }

    if (!Array.isArray(state.page)) {
      return {
        ...state,
        page: [state.page],
      };
    }

    return state;
  }

  /**
   * Retrieves the initial snapshot from the state
   *
   * @return {ApiLoadConsentResponse | undefined} The first snapshot in the state, or undefined if no snapshots exist
   */
  getInitialSnapshot() {
    const state = this.getState();
    return state?.page[0];
  }

  /**
   * Retrieves the previous snapshot from the state
   *
   * @return {ApiLoadConsentResponse | undefined} The second-to-last snapshot in the state, or undefined if fewer than 2 snapshots exist
   */
  getPreviousSnapshot() {
    const state = this.getState();
    return state.page.length > 1 ? state.page[state.page.length - 2] : undefined;
  }

  /**
   * Retrieves the most recent snapshot from the state
   *
   * @return {ApiLoadConsentResponse | undefined} The last snapshot in the state, or undefined if no snapshots exist
   */
  getLastSnapshot() {
    const state = this.getState();
    return state.page[state.page.length - 1];
  }

  /**
   * Saves the current state of the ComsStore as a new snapshot
   *
   * Adds the current page snapshot to the state and saves it to SessionStorage
   */
  saveSnapshot() {
    const state = this.getState();
    state.page.push(this.comsStore.getPageSnapshot());
    Coms4csState.setState(state);
  }

  /**
   * Clears the state in SessionStorage
   *
   * Removes all stored snapshots and state information
   */
  reset() {
    Coms4csState.setState();
  }

  /**
   * Removes the specified number of most recent snapshots from the state
   *
   * @param num - The number of snapshots to remove (1 or 2)
   */
  removeLastSnapshots(num: 1 | 2) {
    const state = this.getState();
    if (state.page.length > num - 1) {
      state.page.splice(-num);
      Coms4csState.setState(state);
    }
  }
}
